<template>
  <div class="video-editor p-[10px] sm:p-[20px]">
    <!-- 主预览区域 -->
    <!--  !h-[calc(100vh-264px-16px-16px-64px-32px)] sm:!h-[calc(100vh-152px-40px-40px-64px-32px)] -->
    <div class="editor-main">
      <VideoPlayer :src="store.videoFileUrl" :currentTime="store.currentTime" :isPlaying="store.isPlaying"
        :startTime="store.startTime" :endTime="store.endTime" :duration="store.duration"
        @duration-change="handleDurationChange" @time-update="handleTimeUpdate" @toggle-play="togglePlay"
        ref="videoPlayer" />
    </div>

    <!-- 底部时间轴区域 -->
    <div class="flex-1 min-h-[264px] sm:min-h-fit">
      <div class="timeline-container">
        <TimelineSlider :frames="store.videoFrames" :duration="store.duration" :onChange="handleTimelineChange"
          :minDistance="props.minTrimDuration" />
      </div>

      <!-- 底部控制栏 -->
      <div class="mt-4 bottom-controls">
        <div class="time-input-wrapper">
          <TimeInput v-model:startTime="store.startTime" v-model:endTime="store.endTime" :duration="store.duration" />
        </div>
        <div class="confirm-btn-wrapper">
          <v-btn @click="handleClose"
            class="mx-auto w-full h-10 text-orange-600 rounded-full sm:w-auto ms-2 bg-orange-600/20 lg:h-12 min-w-[140px]"
            size="large">
            <span>Close</span>
          </v-btn>
          <v-btn @click="handleTimeConfirm" color="primary"
            class=" flex-1 mx-auto w-full h-10 rounded-full sm:w-auto text-neutral-950 lg:h-12 min-w-[140px]"
            size="large" style="color: #000 !important;">
            <span>Confirm</span>
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useVideoEditorStore } from './store'
import VideoPlayer from './VideoPlayer.vue'
import appConfig from '~/app.config';
import TimelineSlider from './TimelineSlider.vue'
import TimeInput from './TimeInput.vue'

const store = useVideoEditorStore()
const videoPlayer = ref()
const { $logInfo }: any = useNuxtApp();
let clip: any = null
interface Props {
  videoFile: File | null // 视频文件
  frameCount?: number // 视频帧数
  minTrimDuration?: number // 最小裁剪时长，毫秒
}

const props = withDefaults(defineProps<Props>(), {
  frameCount: 12,
  minTrimDuration: appConfig.maxTrimVideoDuration,
})

// 视频控制方法
const togglePlay = async () => {
  if (!videoPlayer.value) return

  try {
    if (store.isPlaying) {
      await videoPlayer.value.pause()
    } else {
      // store.currentTime < store.startTime ||
      console.log('togglePlay', store.startTime, store.currentTime, store.endTime)
      if (store.currentTime >= store.endTime) {
        store.setCurrentTime(store.startTime)
        if (videoPlayer.value?.videoRef) {
          console.log('reset to start time', store.startTime)
          videoPlayer.value.videoRef.currentTime = store.startTime
        }
      }
      await videoPlayer.value.play()
    }
    store.setIsPlaying(!store.isPlaying)
  } catch (error) {
    console.error('Failed to toggle play state:', error)
  }
}

// 处理视频时长变化
const handleDurationChange = (newDuration: number) => {
  const durationInMicroseconds = Math.floor(newDuration * 1000000)
  store.setDuration(durationInMicroseconds)
  store.setTimeRange(0, durationInMicroseconds)
}

// 处理视频播放时间更新
const handleTimeUpdate = (seconds: number) => {
  // 将秒转换为微秒
  const timeInMicroseconds = Math.floor(seconds * 1000000)

  if (timeInMicroseconds >= store.endTime) {
    // 如果当前时间大于结束时间，则将当前时间设置为结束时间
    // videoPlayer.value.pause()
    store.updateVideoTime(store.startTime)
    return;
  }

  // 如果当前时间小于开始时间，则将当前时间设置为开始时间
  if (timeInMicroseconds < store.startTime) {
    store.updateVideoTime(store.startTime)
  } else {
    // 否则，将当前时间设置为微秒时间
    store.setCurrentTime(timeInMicroseconds)
    // store.updateVideoTime(timeInMicroseconds)
  }
}

// 处理视频帧
const processVideoFrames = async (url: string) => {
  if (store.isProcessingFrames || !url) return
  store.setProcessingFrames(true)
  try {
    const { MP4Clip } = await import('@webav/av-cliper')
    const response = await fetch(url)
    if (!response.body) throw new Error('Failed to fetch video')

    clip = new MP4Clip(response.body)
    await clip.ready

    const durationInMicroseconds = Math.floor(clip.meta.duration)
    store.setDuration(durationInMicroseconds)

    const initialEndTime = Math.min(durationInMicroseconds, props.minTrimDuration)
    store.setTimeRange(0, initialEndTime)

    const desiredFrameCount = props.frameCount
    const step = Math.floor(clip.meta.duration / (desiredFrameCount - 1))

    const frames = await clip.thumbnails(160, {
      start: 0,
      end: clip.meta.duration,
      step: step
    })

    store.setVideoFrames(frames)
  } catch (error) {
    console.error('处理视频帧失败:', error)
  } finally {
    store.setProcessingFrames(false)
  }
}

// 处理时间轴变化
const handleTimelineChange = (start: number, end: number) => {
  const startInMicroseconds = Math.floor(start)
  const endInMicroseconds = Math.floor(end)
  store.setTimeRange(startInMicroseconds, endInMicroseconds)
}

// 处理时间轴确认
const handleTimeConfirm = async () => {
  try {
    const durationInSeconds = (store.endTime - store.startTime) / 1000000
    $logInfo(`视频时长: ${durationInSeconds.toFixed(2)}秒`);
    emit('confirm-trim-end', {
      start: store.startTime,
      end: store.endTime,
      videoFileUrl: store.videoFileUrl
    })

    // 下载视频
    // const a = document.createElement('a')
    // a.href = URL.createObjectURL(blob)
    // a.download = `${Date.now()}.mp4`
    // a.click()
  } catch (error) {
    console.error('处理时间轴确认失败:', error)
  } finally {
  }
}

const handleClose = () => {
  emit('close')
}

// 监听视频文件变化
const stopWatches = [
  watch(
    () => props.videoFile,
    async (newFile) => {
      if (newFile) {
        store.setVideoFileUrl(null)
        await nextTick()
        const url = URL.createObjectURL(newFile)
        store.setVideoFileUrl(url)
        await processVideoFrames(url)
      } else {
        if (store.videoFileUrl) {
          URL.revokeObjectURL(store.videoFileUrl)
        }
        store.setVideoFileUrl(null)
      }
    },
    { immediate: true, flush: 'post' }
  ),
  // 监听视频结束
  watch(() => store.currentTime, (newTime) => {
    if (newTime >= store.endTime && store.isPlaying) {
      togglePlay()
    }
  })
]

const emit = defineEmits<{
  'confirm-trim-end': [{ start: number, end: number, videoFileUrl: string }],
  'close': []
}>()

defineExpose({
  togglePlay,
  videoPlayer
})

const cleanup = () => {
  // 清理 URL 对象
  if (store.videoFileUrl) {
    URL.revokeObjectURL(store.videoFileUrl)
    store.setVideoFileUrl(null)
  }

  // 清理视频帧
  store.setVideoFrames([])

  // 清理事件监听器
  if (videoPlayer.value?.videoRef) {
    videoPlayer.value.videoRef.removeEventListener('timeupdate', handleTimeUpdate)
    videoPlayer.value.videoRef.removeEventListener('durationchange', handleDurationChange)
  }

  // 停止所有 watch
  stopWatches.forEach(stop => stop())

  // 清理 MP4Clip 实例
  if (clip) {
    clip.destroy?.()
    clip = null
  }

  // 重置 store 状态
  // store.$reset?.()
}

onBeforeUnmount(cleanup)
</script>

<style scoped>
.video-editor {
  display: flex;
  height: 100%;
  flex-direction: column;
  background: #1e1e1e;
  color: white;
  /* margin: 8px; */
  /* margin: 16px; */

}

.editor-main {
  /* margin-top: 56px; */
  /* flex: 1; */
  min-height: 0;
}




.bottom-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 8px;
  border-radius: 8px;
  flex-wrap: wrap;
  gap: 16px;
}

.time-input-wrapper {
  flex: 1;
  min-width: 280px;
}

.confirm-btn-wrapper {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: center;
  min-width: 120px;
}

@media (max-width: 640px) {
  .video-editor {
    padding: 8px;
  }

  .editor-main {
    margin-bottom: 8px;
  }

  .timeline-container {
    /* height: 80px; */
    margin: 8px 0;
  }

  .bottom-controls {
    padding: 4px;
    /* gap: 8px; */
  }

  .time-input-wrapper {
    min-width: 100%;
  }

  .confirm-btn-wrapper {
    width: 100%;
  }

  .v-btn {
    width: 100% !important;
    height: 44px !important;
  }
}
</style>